
import React, { createContext, useContext, useState, useEffect } from 'react';
import { User, EnterpriseInfo, UserTier, Employee, PointTransaction, AuditLog, VideoTask } from '../types';
import { getUserToken, USER_TOKEN_KEY } from '../utils/authToken';

interface Notification {
  id: number;
  type: string;
  content: string;
  status: string;
  created_at: string;
}

interface AuthContextType {
  user: User | null;
  enterpriseInfo: EnterpriseInfo | null;
  login: (email: string, password?: string) => Promise<void>; 
  logout: () => void;
  updatePoints: (amount: number) => void;
  isLoading: boolean;
  upgradeTier: (tier: UserTier) => Promise<void>;
  register: (email: string, password?: string) => Promise<void>;
  registerEnterprise: (companyName: string, email: string, password?: string) => Promise<void>;
  addEmployee: (email: string) => Promise<void>;
  removeEmployee: (id: string) => Promise<void>;
  transferPoints: (toUserId: string, amount: number, type: 'ALLOCATE' | 'RECOVER') => Promise<void>;
  getEnterpriseEmployees: () => Promise<Employee[]>;
  getEnterpriseLogs: () => Promise<{ transactions: PointTransaction[], tasks: VideoTask[] }>;
  submitEnterpriseVerify: (licenseUrl: string) => Promise<void>;
  
  // Notification related
  notifications: Notification[];
  fetchNotifications: () => Promise<void>;
  respondNotification: (id: number, action: 'accept' | 'reject') => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);
// API 基址：强制走本地 /api 代理（避免错误指向外部域名）
const API_BASE = '/api';
const ENABLE_NOTIFICATIONS = import.meta.env.VITE_ENABLE_NOTIFICATIONS === 'true';

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [enterpriseInfo, setEnterpriseInfo] = useState<EnterpriseInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notifications, setNotifications] = useState<Notification[]>([]);

  useEffect(() => {
    const initAuth = async () => {
      const savedUser = localStorage.getItem('tyak_user');
      const token = localStorage.getItem(USER_TOKEN_KEY);
      
      if (savedUser && token) {
        try {
          // 验证 token 是否有效
          const res = await fetch(`${API_BASE}/auth/me`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (res.ok) {
            const data = await res.json();
            if (data.code === 200 && data.user) {
              // 验证成功，使用后端返回的最新用户数据
              setUser(data.user);
              localStorage.setItem('tyak_user', JSON.stringify(data.user));
              setIsLoading(false);
              return;
            }
          }
          
          // 如果后端返回 401/404 等错误，仍然使用本地数据（保守策略）
          // 这样可以避免因为临时网络问题或后端配置问题导致用户被登出
          console.warn('Token validation failed, but keeping local user data');
          setUser(JSON.parse(savedUser));
        } catch (error) {
          // 网络错误或其他异常，也使用本地数据
          console.error('Token validation error, keeping local user data:', error);
          setUser(JSON.parse(savedUser));
        }
      }
      
      setIsLoading(false);
    };
    
    initAuth();
  }, []);

  const getHeaders = () => {
    const token = getUserToken();
    return {
      'Content-Type': 'application/json',
      // 保持原行为：token 为空时会变成 "Bearer null"
      'Authorization': `Bearer ${token}`
    };
  };

  const login = async (email: string, password?: string) => {
    try {
      const res = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password: password || '123456' })
      });
      
      if (!res.ok) {
        // 可能返回 HTML(如 404)，避免 JSON 解析报错
        const text = await res.text();
        let msg = '登录验证失败，请确认账号密码';
        try {
          const parsed = JSON.parse(text);
          msg = parsed.msg || msg;
        } catch (_) {
          if (res.status === 404) msg = '登录接口未找到，请检查 API_BASE 是否正确';
        }
        throw new Error(msg);
      }
      
      const data = await res.json();
      setUser(data.user);
      
      // 如果是企业主账号，初始化企业信息（Mock data for now, real data comes from users table fields）
      if (data.user.tier === 'Team' && data.user.role === 'Owner') {
          setEnterpriseInfo({
              id: data.user.id,
              companyName: data.user.name,
              contactName: data.user.real_name || data.user.name,
              contactEmail: data.user.email,
              balance: data.user.wallet_balance
          });
      }

      localStorage.setItem(USER_TOKEN_KEY, data.token);
      localStorage.setItem('tyak_user', JSON.stringify(data.user));
    } catch (e: any) {
      throw new Error(e.message);
    }
  };

  const logout = () => {
    setUser(null);
    setEnterpriseInfo(null);
    localStorage.removeItem(USER_TOKEN_KEY);
    localStorage.removeItem('tyak_user');
  };

  const updatePoints = (amount: number) => {
    if (user) {
      const newUser = { ...user, points: user.points + amount };
      setUser(newUser);
      localStorage.setItem('tyak_user', JSON.stringify(newUser));
    }
  };

  const upgradeTier = async (tier: UserTier) => {
    if (user) {
      const newUser = { ...user, tier };
      setUser(newUser);
      localStorage.setItem('tyak_user', JSON.stringify(newUser));
    }
  };

  const register = async (email: string, password?: string) => {
    const res = await fetch(`${API_BASE}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        email, 
        password,
        tier: 'Free', // 默认个人是 Free
        name: email.split('@')[0] // 默认昵称
      })
    });

    const data = await res.json();
    if (!res.ok) {
      throw new Error(data.msg || "注册失败，请检查邮箱是否已被使用");
    }
  };

  const registerEnterprise = async (companyName: string, email: string, password?: string) => {
    const res = await fetch(`${API_BASE}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email,
        password,
        name: companyName, // 传入企业名称
        tier: 'Team' // 标记为企业版
      })
    });

    const data = await res.json();
    if (!res.ok) {
      throw new Error(data.msg || "企业注册失败，请检查信息");
    }
  };

  // --- Phase 2 New Methods ---

  const addEmployee = async (email: string) => {
    const res = await fetch(`${API_BASE}/enterprise/invite`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ email })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.msg);
  };

  const removeEmployee = async (id: string) => {
    const res = await fetch(`${API_BASE}/enterprise/employees/${id}`, {
      method: 'DELETE',
      headers: getHeaders()
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.msg);
  };

  const transferPoints = async (toUserId: string, amount: number, type: 'ALLOCATE' | 'RECOVER') => {
    const res = await fetch(`${API_BASE}/enterprise/transfer`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ targetUserId: toUserId, amount, type })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.msg);
    
    // Refresh user balance locally
    if (user) {
        const diff = type === 'ALLOCATE' ? -amount : amount;
        const newUser = { ...user, wallet_balance: Number(user.wallet_balance) + diff };
        setUser(newUser);
        localStorage.setItem('tyak_user', JSON.stringify(newUser));
    }
  };

  const getEnterpriseEmployees = async () => {
    const res = await fetch(`${API_BASE}/enterprise/employees`, {
      headers: getHeaders()
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.msg);
    return data.data as Employee[];
  };

  const getEnterpriseLogs = async () => {
    const res = await fetch(`${API_BASE}/enterprise/stats`, {
        headers: getHeaders()
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.msg);
    return data.data; 
  };

  const submitEnterpriseVerify = async (url: string) => {
    if (enterpriseInfo) {
      setEnterpriseInfo({ ...enterpriseInfo, verified: true, businessLicenseUrl: url });
    }
  };

  const fetchNotifications = async () => {
      if (!ENABLE_NOTIFICATIONS || !user) return;
      try {
          const res = await fetch(`${API_BASE}/notifications`, { headers: getHeaders() });
          if (!res.ok) return;
          const data = await res.json();
          if (data.code === 200) {
              setNotifications(data.data);
          }
      } catch(e) {
        // 静默失败，避免控制台污染
      }
  };

  const respondNotification = async (id: number, action: 'accept' | 'reject') => {
      const res = await fetch(`${API_BASE}/notifications/${id}/respond`, {
          method: 'POST',
          headers: getHeaders(),
          body: JSON.stringify({ action })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.msg);
      // Refresh list
      fetchNotifications();
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      enterpriseInfo,
      login, 
      logout, 
      updatePoints, 
      isLoading,
      upgradeTier,
      register,
      registerEnterprise,
      addEmployee,
      removeEmployee,
      transferPoints,
      getEnterpriseEmployees,
      getEnterpriseLogs,
      submitEnterpriseVerify,
      notifications,
      fetchNotifications,
      respondNotification
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};
